import { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { performHardLogout, useAuthStore } from './store';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/NewDashboard';
import Test from './pages/Test';
import ListeningTest from './pages/ListeningTest';
import ReadingTest from './pages/ReadingTest';
import WritingTest from './pages/WritingTest';
import RandomTest from './pages/RandomTest';
import HSK3Simulation from './pages/HSK3SimulationTest';
import HSK30SystemLevel, { CSCASystemLevel } from './pages/HSK30SystemLevel';
import Workbook from './pages/Workbook';
import CSCAMathWorkbook from './pages/CSCAMathWorkbook';
import CSCAPlacement from './pages/CSCAPlacement';
import AdminCSCAPlacement from './pages/AdminCSCAPlacement';
import CSCAHome from './pages/CSCAHome';
import CSCAUpcoming from './pages/CSCAUpcoming';
import CSCAPastPapers from './pages/CSCAPastPapers';
import CSCAExam from './pages/CSCAExam';
import CSCATopicAnalysis from './pages/CSCATopicAnalysis';
import CSCARandomPaper from './pages/CSCARandomPaper';
import CSCAMoocRandom from './pages/CSCAMoocRandom';
import TestHistory from './pages/TestHistory';
import AdminPanel from './pages/AdminPanel';
import AdminStudentDetail from './pages/AdminStudentDetail';
import AdminStudentHistory from './pages/AdminStudentHistory';
import AdminStudentWrongHistory from './pages/AdminStudentWrongHistory';
import AdminW1GrammarCategories from './pages/AdminW1GrammarCategories';
import AdminTestDetail from './pages/AdminTestDetail';
import AdminQuestions from './pages/AdminQuestions';
import AdminQuestionApprovals from './pages/AdminQuestionApprovals';
import AdminMedia from './pages/AdminMedia';
import VocabLibrary from './pages/VocabLibrary';
import TestVocabReview from './pages/TestVocabReview';
import ExamStats from './pages/ExamStats';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { canAccessLearnerArea, canAccessQuestionApproval, canAccessQuestionManagement, canAccessMediaManagement, canAccessStudentManagement, canAccessVocabManagement, getDefaultAuthenticatedRoute, isAdminRole } from './utils/permissions';

const AUTO_LOGOUT_MS = 29.5 * 24 * 60 * 60 * 1000;

async function drainPendingSubmissions(token: string) {
  const { getPendingSubmissions, removePendingSubmission } = await import('./utils/pendingSubmissions');
  const { localApi } = await import('./utils/local-api');
  const pending = getPendingSubmissions();
  for (const sub of pending) {
    try {
      await localApi.submitTest(sub.testSessionId, sub.answers, token);
      removePendingSubmission(sub.id);
    } catch {
      // Will retry on next app load
    }
  }
}

function App() {
  const { isAuthenticated, user, loginAt, setLoginAt, logout, token } = useAuthStore();
  const hasDrained = useRef(false);
  const defaultAuthenticatedRoute = getDefaultAuthenticatedRoute(user?.role);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Existing persisted sessions created before loginAt was introduced get a fresh timer.
    if (!loginAt) {
      setLoginAt(Date.now());
      return;
    }

    const checkLogout = () => {
      const currentLoginAt = useAuthStore.getState().loginAt;
      if (!currentLoginAt) return;
      const elapsed = Date.now() - currentLoginAt;
      if (elapsed >= AUTO_LOGOUT_MS) {
        void performHardLogout({ redirectToLogin: true });
      }
    };

    // Check immediately
    checkLogout();

    // Since AUTO_LOGOUT_MS (2548800000) exceeds the 32-bit int limit for setTimeout (2147483647),
    // we use a periodic interval to check expiration instead of a single long setTimeout.
    const interval = window.setInterval(checkLogout, 60 * 1000); // Check every minute

    return () => window.clearInterval(interval);
  }, [isAuthenticated, loginAt, setLoginAt]);

  // Drain pending submissions once after login
  useEffect(() => {
    if (!isAuthenticated || !token || hasDrained.current) return;
    hasDrained.current = true;
    drainPendingSubmissions(token);
  }, [isAuthenticated, token]);

  return (
    <ErrorBoundary>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <div className="min-h-screen bg-[#EEF2FF]">
          <Routes>
            <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to={defaultAuthenticatedRoute} />} />
            <Route path="/register" element={!isAuthenticated ? <Register /> : <Navigate to={defaultAuthenticatedRoute} />} />
            <Route path="/" element={isAuthenticated ? <Layout /> : <Navigate to="/login" />}>
              <Route index element={canAccessLearnerArea(user?.role) ? <Dashboard /> : <Navigate to="/admin" replace />} />
              <Route path="test/:level/:part" element={canAccessLearnerArea(user?.role) ? <Test /> : <Navigate to="/admin" replace />} />
              <Route path="test/review/:id" element={canAccessLearnerArea(user?.role) ? <TestVocabReview /> : <Navigate to="/admin" replace />} />
              <Route path="test/:level/listening" element={canAccessLearnerArea(user?.role) ? <ListeningTest /> : <Navigate to="/admin" replace />} />
              <Route path="test/:level/reading" element={canAccessLearnerArea(user?.role) ? <ReadingTest /> : <Navigate to="/admin" replace />} />
              <Route path="test/:level/writing" element={canAccessLearnerArea(user?.role) ? <WritingTest /> : <Navigate to="/admin" replace />} />
              <Route path="test/random" element={canAccessLearnerArea(user?.role) ? <RandomTest /> : <Navigate to="/admin" replace />} />
              <Route path="simulation/:examLevel" element={canAccessLearnerArea(user?.role) ? <HSK3Simulation /> : <Navigate to="/admin" replace />} />
              <Route path="hsk3/workbook/:examLevel" element={canAccessLearnerArea(user?.role) ? <Workbook /> : <Navigate to="/admin" replace />} />
              <Route path="test/:level/workbook" element={canAccessLearnerArea(user?.role) ? <Workbook /> : <Navigate to="/admin" replace />} />
              <Route path="workbook/history" element={canAccessLearnerArea(user?.role) ? <Workbook /> : <Navigate to="/admin" replace />} />
              <Route path="hsk3/simulation/:examLevel" element={canAccessLearnerArea(user?.role) ? <HSK30SystemLevel systemType="simulation" /> : <Navigate to="/admin" replace />} />
              <Route path="csca/workbook/3" element={canAccessLearnerArea(user?.role) ? <CSCAMathWorkbook /> : <Navigate to="/admin" replace />} />
              <Route path="csca/test/:examLevel" element={canAccessLearnerArea(user?.role) ? <CSCAPlacement /> : <Navigate to="/admin" replace />} />
              <Route path="csca/workbook/:examLevel" element={canAccessLearnerArea(user?.role) ? <CSCASystemLevel systemType="workbook" /> : <Navigate to="/admin" replace />} />
              <Route path="csca/simulation/1" element={canAccessLearnerArea(user?.role) ? <CSCAHome level={1} /> : <Navigate to="/admin" replace />} />
              <Route path="csca/simulation/2" element={canAccessLearnerArea(user?.role) ? <CSCAHome level={2} /> : <Navigate to="/admin" replace />} />
              <Route path="csca/simulation/3" element={canAccessLearnerArea(user?.role) ? <Navigate to="/csca" replace /> : <Navigate to="/admin" replace />} />
              <Route path="csca/simulation/1/papers" element={canAccessLearnerArea(user?.role) ? <CSCAPastPapers fixedLevel={1} /> : <Navigate to="/admin" replace />} />
              <Route path="csca/simulation/2/papers" element={canAccessLearnerArea(user?.role) ? <CSCAPastPapers fixedLevel={2} /> : <Navigate to="/admin" replace />} />
              <Route path="csca/simulation/3/papers" element={canAccessLearnerArea(user?.role) ? <CSCAPastPapers fixedLevel={3} /> : <Navigate to="/admin" replace />} />
              <Route path="csca/simulation/:examLevel/upcoming/:feature" element={canAccessLearnerArea(user?.role) ? <CSCAUpcoming /> : <Navigate to="/admin" replace />} />
              <Route path="csca/simulation/1/topics" element={canAccessLearnerArea(user?.role) ? <CSCATopicAnalysis fixedLevel={1} /> : <Navigate to="/admin" replace />} />
              <Route path="csca/simulation/2/topics" element={canAccessLearnerArea(user?.role) ? <CSCATopicAnalysis fixedLevel={2} /> : <Navigate to="/admin" replace />} />
              <Route path="csca/simulation/3/topics" element={canAccessLearnerArea(user?.role) ? <CSCATopicAnalysis fixedLevel={3} /> : <Navigate to="/admin" replace />} />
              <Route path="csca/simulation/:examLevel" element={canAccessLearnerArea(user?.role) ? <CSCASystemLevel systemType="simulation" /> : <Navigate to="/admin" replace />} />
              <Route path="csca" element={canAccessLearnerArea(user?.role) ? <CSCAHome /> : <Navigate to="/admin" replace />} />
              <Route path="csca/past-papers" element={canAccessLearnerArea(user?.role) ? <CSCAPastPapers /> : <Navigate to="/admin" replace />} />
              <Route path="csca/past-papers/:paperId" element={canAccessLearnerArea(user?.role) ? <CSCAExam /> : <Navigate to="/admin" replace />} />
              <Route path="csca/topic-analysis" element={canAccessLearnerArea(user?.role) ? <CSCATopicAnalysis /> : <Navigate to="/admin" replace />} />
              <Route path="csca/topic-practice" element={canAccessLearnerArea(user?.role) ? <CSCAExam /> : <Navigate to="/admin" replace />} />
              <Route path="csca/random" element={canAccessLearnerArea(user?.role) ? <CSCARandomPaper /> : <Navigate to="/admin" replace />} />
              <Route path="csca/random/exam" element={canAccessLearnerArea(user?.role) ? <CSCAExam /> : <Navigate to="/admin" replace />} />
              <Route path="csca/mooc/topics" element={canAccessLearnerArea(user?.role) ? <CSCATopicAnalysis source="mooc" /> : <Navigate to="/admin" replace />} />
              <Route path="csca/mooc/topic-practice" element={canAccessLearnerArea(user?.role) ? <CSCAExam /> : <Navigate to="/admin" replace />} />
              <Route path="csca/mooc/random" element={canAccessLearnerArea(user?.role) ? <CSCAMoocRandom /> : <Navigate to="/admin" replace />} />
              <Route path="csca/mooc/random/exam" element={canAccessLearnerArea(user?.role) ? <CSCAExam /> : <Navigate to="/admin" replace />} />
              <Route path="history" element={canAccessLearnerArea(user?.role) ? <TestHistory /> : <Navigate to="/admin" replace />} />
              <Route path="stats" element={canAccessLearnerArea(user?.role) ? <ExamStats /> : <Navigate to="/admin" replace />} />
              <Route path="admin" element={canAccessStudentManagement(user?.role) ? <AdminPanel /> : <Navigate to="/" />} />
              <Route path="admin/csca-placement" element={canAccessStudentManagement(user?.role) ? <AdminCSCAPlacement /> : <Navigate to="/" />} />
              <Route path="admin/questions" element={canAccessQuestionManagement(user?.role) ? <AdminQuestions /> : <Navigate to="/" />} />
              <Route path="admin/question-approvals" element={canAccessQuestionApproval(user?.role) ? <AdminQuestionApprovals /> : <Navigate to="/" />} />
              <Route path="admin/media" element={canAccessMediaManagement(user?.role) ? <AdminMedia /> : <Navigate to="/" />} />
              <Route path="admin/vocab" element={canAccessVocabManagement(user?.role) ? <VocabLibrary /> : <Navigate to="/" />} />
              <Route path="admin/users/:id" element={canAccessStudentManagement(user?.role) ? <AdminStudentDetail /> : <Navigate to="/" />} />
              <Route path="/admin/users/:id/history" element={canAccessStudentManagement(user?.role) ? <AdminStudentHistory /> : <Navigate to="/" />} />
              <Route path="/admin/users/:id/wrong-history" element={canAccessStudentManagement(user?.role) ? <AdminStudentWrongHistory /> : <Navigate to="/" />} />
              <Route path="/admin/w1-grammar-categories" element={isAdminRole(user?.role) ? <AdminW1GrammarCategories /> : <Navigate to="/admin" />} />
              <Route path="/admin/test/:id" element={canAccessStudentManagement(user?.role) ? <AdminTestDetail /> : <Navigate to="/" />} />
              <Route path="/vocab" element={canAccessLearnerArea(user?.role) ? <VocabLibrary /> : <Navigate to="/admin" replace />} />
            </Route>
          </Routes>
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
